import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Import Shared Components
import { ActionBar, Loader } from 'shared-components';
import { useUpdateStateObject } from 'shared-components/src/hooks/useUpdateStateObject';
// Import Global Components
import SubmitButton from '../../../components/SubmitButton';
// Import Global Layouts
import Form from '../../../layouts/Form';
// Import Global Utils
import { profileFieldError, contactFields, addressFields } from '../../../utils/profile';
// Import Local Components
import ProfileForm from '../components/Form';

const ProfileEdit = ({ onSubmit, cardholder, addressReadOnly, ...props }) => {
  // Define States
  const [loading, setLoading] = useState(true);
  // Define Reducers
  const [cardholderInput, handleOnChange] = useUpdateStateObject({});
  const [validationErrors, setValidationErrors] = useState({});

  const checkedFields = addressReadOnly ? contactFields : [...contactFields, ...addressFields];

  useEffect(() => {
    Object.keys(cardholder).map(k => {
      handleOnChange({ key: k, value: cardholder[k] });
      return null;
    });
    setLoading(false);
  }, [cardholder, handleOnChange]);

  if (loading) return <Loader />;

  return (
    // noValidate: the browser's own messages ("Please match the requested format.")
    // follow the browser's language, not the portal's. profileFieldError covers the
    // same rules in translated text.
    <Form
      noValidate
      onSubmit={e => {
        e.preventDefault();
        const errors = checkedFields.reduce(
          (acc, field) => ({ ...acc, [field]: profileFieldError(field, cardholderInput) }),
          {}
        );
        setValidationErrors(errors);
        if (Object.values(errors).some(Boolean)) return;
        onSubmit(cardholderInput);
      }}
    >
      <ProfileForm
        iconType="x"
        cardholder={cardholderInput}
        storedPhone={cardholder.phone}
        addressReadOnly={addressReadOnly}
        onChange={change => {
          handleOnChange(change);
          // A field already flagged is rechecked as it is corrected, so the message
          // clears once the value is right rather than on the next submit.
          if (validationErrors[change.key]) {
            setValidationErrors(errors => ({
              ...errors,
              [change.key]: profileFieldError(change.key, { [change.key]: change.value }),
            }));
          }
        }}
        onBlur={field => {
          if (!checkedFields.includes(field)) return;
          setValidationErrors(errors => ({
            ...errors,
            [field]: profileFieldError(field, cardholderInput),
          }));
        }}
        validationErrors={validationErrors}
        {...props}
      />
      <ActionBar>
        <SubmitButton
          disabled={
            JSON.stringify(cardholderInput) === JSON.stringify(cardholder)
          }
        />
      </ActionBar>
    </Form>
  );
};

ProfileEdit.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  addressReadOnly: PropTypes.bool,
  cardholder: PropTypes.objectOf({
    address1: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    country: PropTypes.string,
    email: PropTypes.string,
    first_name: PropTypes.string,
    id: PropTypes.number,
    last_name: PropTypes.string,
    middle_name: PropTypes.string,
    phone: PropTypes.string,
    postal_code: PropTypes.string,
  }).isRequired,
};

ProfileEdit.defaultProps = {
  addressReadOnly: false,
};

export default ProfileEdit;
